"use client";

import React from "react";
import { Loader, Send, CircleCheck, CircleAlert, Phone, MessageCircle, Clock, ShieldCheck } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
function ContactFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center animate-in fade-in zoom-in duration-500">
        <div className="size-16 bg-primary/10 rounded-lg flex items-center justify-center mb-6">
          <CircleCheck className="size-8 text-primary" />
        </div>
        <h3 className="text-2xl font-bold font-default text-foreground mb-2">
          ¡Mensaje Enviado!
        </h3>
        <p className="text-muted-foreground mb-8 max-w-md">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="min-w-[200px] rounded-md font-default">
          Enviar otro mensaje
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} noValidate className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="name" rules={{
        required: "El nombre es obligatorio",
        minLength: {
          value: 2,
          message: "El nombre debe tener al menos 2 caracteres"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-default">Nombre Completo</FormLabel>
              <FormControl>
                <Input placeholder="Ej. Juan Pérez" className="border-border rounded-sm bg-transparent focus:ring-primary" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "El correo es obligatorio",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Ingrese un correo electrónico válido"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-default">Correo Electrónico</FormLabel>
              <FormControl>
                <Input type="email" placeholder="Ej. Juan Pérez" className="border-border rounded-sm bg-transparent focus:ring-primary" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        pattern: {
          value: /^[0-9+\-\s()]*$/,
          message: "Ingrese un número de teléfono válido"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-default">Teléfono (Opcional)</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="Ej. Juan Pérez" className="border-border rounded-sm bg-transparent focus:ring-primary" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="subject" rules={{
        required: "El asunto es obligatorio",
        minLength: {
          value: 3,
          message: "El asunto debe tener al menos 3 caracteres"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-default">Asunto</FormLabel>
              <FormControl>
                <Input placeholder="Ej. Juan Pérez" className="border-border rounded-sm bg-transparent focus:ring-primary" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <div className="md:col-span-2">
          <FormField control={control} name="message" rules={{
          required: "El mensaje es obligatorio",
          minLength: {
            value: 10,
            message: "El mensaje debe tener al menos 10 caracteres"
          }
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="text-foreground font-default">Mensaje</FormLabel>
                <FormControl>
                  <Textarea placeholder="Cuéntenos más sobre su requerimiento de telecomunicaciones..." className="min-h-[150px] resize-y border-border rounded-sm bg-transparent focus:ring-primary" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />
        </div>
      </div>

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md flex items-start gap-3">
          <CircleAlert className="size-5 text-destructive shrink-0 mt-0.5" />
          <p className="text-sm text-destructive font-medium">{submitError}</p>
        </div>}

      <div className="flex justify-end pt-2">
        <Button type="submit" disabled={isSubmitting} className="md:w-auto min-w-[200px] bg-[#333333] text-white hover:opacity-90 rounded-md font-default font-bold transition-all shadow-sm" size="lg">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-5" />
              Enviando...
            </> : <>
              <Send className="mr-2 size-5" />
              Enviar Mensaje
            </>}
        </Button>
      </div>
    </Form>;
}
export default function AgendeUnaAsesorA() {
  return <section data-section-id="253898" id="agende-asesoria-form" className="bg-background text-foreground scroll-mt-48 !m-0 ![padding:10px_90px_10px_0px]">
      <div className="container mx-auto px-4 md:px-6 max-w-6xl">
        <div className="text-center mb-12 md:mb-16">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold font-default text-primary mb-4 tracking-tight">
            Agende una Asesoría
          </h2>
          <p className="text-lg text-foreground max-w-2xl mx-auto font-default">
            ¿Tiene alguna pregunta o desea optimizar sus comunicaciones? Complete el formulario o contáctenos de forma directa.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
          <Card className="lg:col-span-2 shadow-md border-border/50 overflow-hidden rounded-lg bg-white">
            <CardContent className="p-6 md:p-8 lg:p-10">
              <FormProvider formKey="agende_asesoria_form_home" formId="agende_asesoria_form_home" sectionName="Contact Section" onSuccess={() => console.log("Form submitted successfully")} onError={error => console.error("Form submission error:", error)} defaultValues={{
              name: "",
              email: "",
              phone: "",
              subject: "",
              message: ""
            }} validationRules={{
              name: {
                required: true,
                minLength: 2
              },
              email: {
                required: true,
                pattern: "email"
              },
              phone: {
                required: false
              },
              subject: {
                required: true,
                minLength: 3
              },
              message: {
                required: true,
                minLength: 10
              }
            }} submitText="Enviar Mensaje" successMessage="¡Gracias por contactarnos! Hemos recibido su mensaje y nos comunicaremos con usted pronto.">
                <ContactFormInner />
              </FormProvider>
            </CardContent>
          </Card>

          <Card className="shadow-md border-border/50 overflow-hidden rounded-lg bg-white flex flex-col justify-between">
            <CardHeader className="p-6 pb-4">
              <CardTitle className="text-xl font-bold font-default text-foreground">
                Contacto Inmediato
              </CardTitle>
              <CardDescription className="text-muted-foreground font-default text-sm">
                ¿Prefiere una respuesta inmediata? Comuníquese directamente con nuestros especialistas a través de nuestros canales directos.
              </CardDescription>
            </CardHeader>

            <CardContent className="p-6 pt-2 space-y-4">
              <div className="space-y-3">
                <Button asChild size="lg" className="w-full bg-[#142d55] text-white hover:bg-[#102444] rounded-md font-default font-semibold shadow-sm justify-center">
                  <a href="tel:+50622633278">
                    <Phone className="size-4" />
                    <span>Llamar Ahora</span>
                  </a>
                </Button>

                <Button asChild size="lg" className="w-full bg-[#25D366] text-white hover:bg-[#20be5c] rounded-md font-default font-semibold shadow-sm justify-center">
                  <Link to="https://wa.me/50687176886" newTab>
                    <MessageCircle className="size-4" />
                    <span>WhatsApp</span>
                  </Link>
                </Button>
              </div>

              <div className="pt-4 border-t border-border/60 space-y-3 text-xs text-muted-foreground font-default">
                <div className="flex items-center gap-2">
                  <Clock className="size-4 text-primary shrink-0" />
                  <span>Horario: Lun - Vie 8:00 AM - 5:00 PM</span>
                </div>
                <div className="flex items-center gap-2">
                  <ShieldCheck className="size-4 text-primary shrink-0" />
                  <span>Atención técnica y comercial garantizada</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>;
}